import { useReducer, useCallback, useMemo } from 'react';
import type { ColumnType } from '../../../types/columnType';
import type {
  DraftChange,
  DraftChangeType,
  DatasetChangeset,
  DatasetRowData,
} from '../../../types/dataGrid';

interface DraftState {
  cellEdits: Map<string, string | number | null>; // "row:col" -> value
  rowsAdded: DatasetRowData[];
  rowsDeleted: Set<number>;
  columnsAdded: { name: string; type: ColumnType; position: number }[];
  columnsDeleted: Set<number>;
  columnsRenamed: Map<number, string>; // columnIndex -> newName
  undoStack: DraftChange[];
  redoStack: DraftChange[];
}

type DraftAction =
  | { type: 'EDIT_CELL'; rowIndex: number; columnIndex: number; value: string | number | null; previousValue: string | number | null }
  | { type: 'ADD_ROW'; columnCount: number }
  | { type: 'DELETE_ROWS'; rowIndices: number[] }
  | { type: 'ADD_COLUMN'; name: string; columnType: ColumnType; position: number; totalRows: number }
  | { type: 'DELETE_COLUMNS'; columnIndices: number[] }
  | { type: 'RENAME_COLUMN'; columnIndex: number; newName: string; previousName: string }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'DISCARD_ALL' }
  | { type: 'CLEAR_AFTER_SAVE' };

function cellKey(row: number, col: number): string {
  return `${row}:${col}`;
}

let changeIdCounter = 0;
function nextChangeId(): string {
  return `change_${++changeIdCounter}_${Date.now()}`;
}

function createInitialState(): DraftState {
  return {
    cellEdits: new Map(),
    rowsAdded: [],
    rowsDeleted: new Set(),
    columnsAdded: [],
    columnsDeleted: new Set(),
    columnsRenamed: new Map(),
    undoStack: [],
    redoStack: [],
  };
}

function applyChange(state: DraftState, change: DraftChange): DraftState {
  const newState = { ...state };

  switch (change.type) {
    case 'cell_edit': {
      const key = cellKey(change.target.rowIndex!, change.target.columnIndex!);
      const newEdits = new Map(state.cellEdits);
      newEdits.set(key, change.newValue as string | number | null);
      newState.cellEdits = newEdits;
      break;
    }
    case 'row_add': {
      newState.rowsAdded = [...state.rowsAdded, change.newValue as DatasetRowData];
      break;
    }
    case 'row_delete': {
      const indices = change.newValue as number[];
      const newDeleted = new Set(state.rowsDeleted);
      indices.forEach((i) => newDeleted.add(i));
      newState.rowsDeleted = newDeleted;
      break;
    }
    case 'column_add': {
      const col = change.newValue as { name: string; type: ColumnType; position: number };
      newState.columnsAdded = [...state.columnsAdded, col];
      break;
    }
    case 'column_delete': {
      const indices = change.newValue as number[];
      const newDeleted = new Set(state.columnsDeleted);
      indices.forEach((i) => newDeleted.add(i));
      newState.columnsDeleted = newDeleted;
      break;
    }
    case 'column_rename': {
      const { columnIndex } = change.target;
      const newRenamed = new Map(state.columnsRenamed);
      newRenamed.set(columnIndex!, change.newValue as string);
      newState.columnsRenamed = newRenamed;
      break;
    }
  }

  return newState;
}

function revertChange(state: DraftState, change: DraftChange): DraftState {
  const newState = { ...state };

  switch (change.type) {
    case 'cell_edit': {
      const key = cellKey(change.target.rowIndex!, change.target.columnIndex!);
      const newEdits = new Map(state.cellEdits);
      if (change.previousValue === undefined) {
        newEdits.delete(key);
      } else {
        newEdits.set(key, change.previousValue as string | number | null);
      }
      newState.cellEdits = newEdits;
      break;
    }
    case 'row_add': {
      newState.rowsAdded = state.rowsAdded.slice(0, -1);
      break;
    }
    case 'row_delete': {
      const indices = change.newValue as number[];
      const newDeleted = new Set(state.rowsDeleted);
      indices.forEach((i) => newDeleted.delete(i));
      newState.rowsDeleted = newDeleted;
      break;
    }
    case 'column_add': {
      newState.columnsAdded = state.columnsAdded.slice(0, -1);
      break;
    }
    case 'column_delete': {
      const indices = change.newValue as number[];
      const newDeleted = new Set(state.columnsDeleted);
      indices.forEach((i) => newDeleted.delete(i));
      newState.columnsDeleted = newDeleted;
      break;
    }
    case 'column_rename': {
      const { columnIndex } = change.target;
      const newRenamed = new Map(state.columnsRenamed);
      if (change.previousValue === undefined || change.previousValue === null) {
        newRenamed.delete(columnIndex!);
      } else {
        newRenamed.set(columnIndex!, change.previousValue as string);
      }
      newState.columnsRenamed = newRenamed;
      break;
    }
  }

  return newState;
}

function draftReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case 'EDIT_CELL': {
      const change: DraftChange = {
        id: nextChangeId(),
        type: 'cell_edit',
        target: { rowIndex: action.rowIndex, columnIndex: action.columnIndex },
        previousValue: action.previousValue,
        newValue: action.value,
        timestamp: Date.now(),
      };
      const newState = applyChange(state, change);
      newState.undoStack = [...state.undoStack, change];
      newState.redoStack = [];
      return newState;
    }

    case 'ADD_ROW': {
      const emptyRow: DatasetRowData = {
        row_index: -1, // Will be resolved on save
        values: Array(action.columnCount).fill(null),
      };
      const change: DraftChange = {
        id: nextChangeId(),
        type: 'row_add',
        target: {},
        previousValue: null,
        newValue: emptyRow,
        timestamp: Date.now(),
      };
      const newState = applyChange(state, change);
      newState.undoStack = [...state.undoStack, change];
      newState.redoStack = [];
      return newState;
    }

    case 'DELETE_ROWS': {
      const change: DraftChange = {
        id: nextChangeId(),
        type: 'row_delete',
        target: {},
        previousValue: null,
        newValue: action.rowIndices,
        timestamp: Date.now(),
      };
      const newState = applyChange(state, change);
      newState.undoStack = [...state.undoStack, change];
      newState.redoStack = [];
      return newState;
    }

    case 'ADD_COLUMN': {
      const col = { name: action.name, type: action.columnType, position: action.position };
      const change: DraftChange = {
        id: nextChangeId(),
        type: 'column_add',
        target: { columnName: action.name },
        previousValue: null,
        newValue: col,
        timestamp: Date.now(),
      };
      const newState = applyChange(state, change);
      newState.undoStack = [...state.undoStack, change];
      newState.redoStack = [];
      return newState;
    }

    case 'DELETE_COLUMNS': {
      const change: DraftChange = {
        id: nextChangeId(),
        type: 'column_delete',
        target: {},
        previousValue: null,
        newValue: action.columnIndices,
        timestamp: Date.now(),
      };
      const newState = applyChange(state, change);
      newState.undoStack = [...state.undoStack, change];
      newState.redoStack = [];
      return newState;
    }

    case 'RENAME_COLUMN': {
      const change: DraftChange = {
        id: nextChangeId(),
        type: 'column_rename',
        target: { columnIndex: action.columnIndex },
        previousValue: action.previousName,
        newValue: action.newName,
        timestamp: Date.now(),
      };
      const newState = applyChange(state, change);
      newState.undoStack = [...state.undoStack, change];
      newState.redoStack = [];
      return newState;
    }

    case 'UNDO': {
      if (state.undoStack.length === 0) return state;
      const change = state.undoStack[state.undoStack.length - 1];
      const newState = revertChange(state, change);
      newState.undoStack = state.undoStack.slice(0, -1);
      newState.redoStack = [...state.redoStack, change];
      return newState;
    }

    case 'REDO': {
      if (state.redoStack.length === 0) return state;
      const change = state.redoStack[state.redoStack.length - 1];
      const newState = applyChange(state, change);
      newState.undoStack = [...state.undoStack, change];
      newState.redoStack = state.redoStack.slice(0, -1);
      return newState;
    }

    case 'DISCARD_ALL':
    case 'CLEAR_AFTER_SAVE':
      return createInitialState();

    default:
      return state;
  }
}

export function useDraftState(columnCount: number) {
  const [state, dispatch] = useReducer(draftReducer, undefined, createInitialState);

  const editCell = useCallback(
    (rowIndex: number, columnIndex: number, value: string | number | null, previousValue: string | number | null) => {
      dispatch({ type: 'EDIT_CELL', rowIndex, columnIndex, value, previousValue });
    },
    []
  );

  const addRow = useCallback(() => {
    dispatch({ type: 'ADD_ROW', columnCount });
  }, [columnCount]);

  const deleteRows = useCallback((rowIndices: number[]) => {
    dispatch({ type: 'DELETE_ROWS', rowIndices });
  }, []);

  const addColumn = useCallback(
    (name: string, columnType: ColumnType, totalRows: number) => {
      const position = columnCount + state.columnsAdded.length;
      dispatch({ type: 'ADD_COLUMN', name, columnType, position, totalRows });
    },
    [columnCount, state.columnsAdded.length]
  );

  const deleteColumns = useCallback((columnIndices: number[]) => {
    dispatch({ type: 'DELETE_COLUMNS', columnIndices });
  }, []);

  const renameColumn = useCallback((columnIndex: number, newName: string, previousName: string) => {
    dispatch({ type: 'RENAME_COLUMN', columnIndex, newName, previousName });
  }, []);

  const undo = useCallback(() => dispatch({ type: 'UNDO' }), []);
  const redo = useCallback(() => dispatch({ type: 'REDO' }), []);
  const discardAll = useCallback(() => dispatch({ type: 'DISCARD_ALL' }), []);
  const clearAfterSave = useCallback(() => dispatch({ type: 'CLEAR_AFTER_SAVE' }), []);

  const hasDraftChanges = useMemo(() => {
    return (
      state.cellEdits.size > 0 ||
      state.rowsAdded.length > 0 ||
      state.rowsDeleted.size > 0 ||
      state.columnsAdded.length > 0 ||
      state.columnsDeleted.size > 0 ||
      state.columnsRenamed.size > 0
    );
  }, [state]);

  const canUndo = state.undoStack.length > 0;
  const canRedo = state.redoStack.length > 0;

  const buildChangeset = useCallback((): DatasetChangeset => {
    const cellEdits: DatasetChangeset['cell_edits'] = [];
    state.cellEdits.forEach((value, key) => {
      const [row, col] = key.split(':').map(Number);
      cellEdits.push({ row_index: row, column_index: col, value });
    });

    return {
      cell_edits: cellEdits,
      rows_added: state.rowsAdded.map((r) => ({ values: r.values })),
      rows_deleted: Array.from(state.rowsDeleted),
      columns_added: state.columnsAdded.map((c) => ({ name: c.name, type: c.type, position: c.position })),
      columns_deleted: Array.from(state.columnsDeleted),
      columns_renamed: Array.from(state.columnsRenamed.entries()).map(([column_index, new_name]) => ({
        column_index,
        new_name,
      })),
    };
  }, [state]);

  const getCellValue = useCallback(
    (rowIndex: number, columnIndex: number): string | number | null | undefined => {
      const key = cellKey(rowIndex, columnIndex);
      if (state.cellEdits.has(key)) {
        return state.cellEdits.get(key)!;
      }
      return undefined; // No draft override
    },
    [state.cellEdits]
  );

  const isRowDeleted = useCallback(
    (rowIndex: number): boolean => state.rowsDeleted.has(rowIndex),
    [state.rowsDeleted]
  );

  const isColumnDeleted = useCallback(
    (columnIndex: number): boolean => state.columnsDeleted.has(columnIndex),
    [state.columnsDeleted]
  );

  const getColumnName = useCallback(
    (columnIndex: number, originalName: string): string => {
      return state.columnsRenamed.get(columnIndex) ?? originalName;
    },
    [state.columnsRenamed]
  );

  const isCellDirty = useCallback(
    (rowIndex: number, columnIndex: number): boolean => {
      return state.cellEdits.has(cellKey(rowIndex, columnIndex));
    },
    [state.cellEdits]
  );

  return {
    // State
    hasDraftChanges,
    canUndo,
    canRedo,
    rowsAdded: state.rowsAdded,
    columnsAdded: state.columnsAdded,

    // Actions
    editCell,
    addRow,
    deleteRows,
    addColumn,
    deleteColumns,
    renameColumn,
    undo,
    redo,
    discardAll,
    clearAfterSave,

    // Queries
    buildChangeset,
    getCellValue,
    isRowDeleted,
    isColumnDeleted,
    getColumnName,
    isCellDirty,
  };
}
